import { useModeAwareQuery } from "@/hooks/useModeAwareQuery";
import { useApi } from "@/hooks/useApi";
import type { AgingSummaryApi } from "@/lib/api/analysis";

const demoAgingSummary: AgingSummaryApi = {
  bands: [
    {
      agingBandId: 1,
      label: "Current (0-30 days)",
      bandLabel: "0-30",
      startDays: 0,
      endDays: 30,
      outstandingAmount: 2200000,
      percentage: 36,
      customerCount: 142,
      color: "#22c55e",
    },
    {
      agingBandId: 2,
      label: "31-60 Days",
      bandLabel: "31-60",
      startDays: 31,
      endDays: 60,
      outstandingAmount: 1400000,
      percentage: 23,
      customerCount: 87,
      color: "#eab308",
    },
    {
      agingBandId: 3,
      label: "61-90 Days",
      bandLabel: "61-90",
      startDays: 61,
      endDays: 90,
      outstandingAmount: 900000,
      percentage: 15,
      customerCount: 54,
      color: "#f97316",
    },
    {
      agingBandId: 4,
      label: "91-120 Days",
      bandLabel: "91-120",
      startDays: 91,
      endDays: 120,
      outstandingAmount: 750000,
      percentage: 12,
      customerCount: 38,
      color: "#ef4444",
    },
    {
      agingBandId: 5,
      label: "Over 120 Days",
      bandLabel: "120+",
      startDays: 121,
      endDays: null,
      outstandingAmount: 850000,
      percentage: 14,
      customerCount: 29,
      color: "#dc2626",
    },
  ],
  totalOutstanding: 6100000,
  calculatedAt: new Date().toISOString(),
};

/**
 * Hook to fetch Ageing Analysis data for the Credit Control Dashboard.
 *
 * In **live** mode it calls:
 *   GET /api/v1/analysis/aging-summary?userId={userId}
 *
 * In **demo** mode it returns the hardcoded aging summary data.
 *
 * @param selectedUserId  Pass a user ID string when a specific user is selected,
 *                        or `undefined` for "Everyone".
 */
export function useAgingSummary(selectedUserId?: string) {
  const { apiGet } = useApi();

  return useModeAwareQuery<AgingSummaryApi>(
    ["analysis", "aging-summary", selectedUserId ?? "everyone"],
    async ({ signal }) => {
      const params: Record<string, string> = {};
      if (selectedUserId) {
        params.userId = selectedUserId;
      }

      return apiGet<AgingSummaryApi>(
        "/api/v1/analysis/aging-summary",
        params,
        signal
      );
    },
    demoAgingSummary,
    { staleTime: 2 * 60 * 1000 }
  );
}
